import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaPlus,
  FaClock,
  FaTh,
  FaCog,
} from 'react-icons/fa';
import GlobalSearchApi from '../../api/GlobalSearchApi';
import { displayRecentTitle, filterRecentlyViewed } from '../../utils/recentlyViewed';
import { canAccessSetup } from '../../utils/entitlements';
import {
  highlightText,
  initials,
  objectTone,
} from './globalSearchUi';
import './GlobalSearch.css';

const SUGGESTION_LIMIT = 10;
const PER_OBJECT_ALL = 3;
const PER_OBJECT_SCOPED = 8;

export default function GlobalSearchPalette({
  open,
  query,
  onQueryChange,
  onClose,
  apps = [],
  setupItems = [],
  inputRef,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [allowedObjects, setAllowedObjects] = useState([]);
  const [objectScope, setObjectScope] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);
  const objectScopeRef = useRef(objectScope);
  objectScopeRef.current = objectScope;

  const allowedNames = useMemo(
    () => allowedObjects.map((o) => o.objectName),
    [allowedObjects]
  );

  const scopedAllowedNames = useMemo(() => {
    if (!objectScope) return allowedNames;
    return allowedNames.filter(
      (n) => String(n).toLowerCase() === String(objectScope).toLowerCase()
    );
  }, [allowedNames, objectScope]);

  const recents = useMemo(
    () => filterRecentlyViewed(scopedAllowedNames),
    [scopedAllowedNames, open]
  );

  const q = String(query || '').trim();
  const qLower = q.toLowerCase();
  const typing = q.length >= 2;

  const selectedObjectMeta = useMemo(
    () =>
      allowedObjects.find(
        (o) =>
          String(o.objectName).toLowerCase() === String(objectScope).toLowerCase()
      ) || null,
    [allowedObjects, objectScope]
  );

  const scopeChips = useMemo(() => {
    const chips = [
      { key: '', label: 'All objects' },
      ...allowedObjects.map((o) => ({
        key: o.objectName,
        label: o.pluralLabel || o.objectLabel || o.objectName,
      })),
    ];
    return chips;
  }, [allowedObjects]);

  const filteredApps = useMemo(() => {
    if (objectScope) return [];
    if (!qLower) return (apps || []).slice(0, 5);
    return (apps || [])
      .filter((a) => String(a.name || '').toLowerCase().includes(qLower))
      .slice(0, 5);
  }, [apps, qLower, objectScope]);

  const filteredSetup = useMemo(() => {
    if (objectScope) return [];
    if (!canAccessSetup()) return [];
    const items = setupItems || [];
    if (!qLower) return items.slice(0, 4);
    return items
      .filter(
        (s) =>
          String(s.label || s.id || '')
            .toLowerCase()
            .includes(qLower)
      )
      .slice(0, 5);
  }, [setupItems, qLower, objectScope]);

  const newActions = useMemo(() => {
    let list = (allowedObjects || []).filter((o) => o.canCreate);
    if (objectScope) {
      list = list.filter(
        (o) =>
          String(o.objectName).toLowerCase() === String(objectScope).toLowerCase()
      );
    }
    if (!qLower) return list.slice(0, 4);
    return list
      .filter((o) => {
        const hay = `${o.objectLabel} ${o.pluralLabel} new`.toLowerCase();
        return hay.includes(qLower) || qLower.startsWith('new');
      })
      .slice(0, 5);
  }, [allowedObjects, qLower, objectScope]);

  const viewAllHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('q', q);
    if (objectScope) params.set('object', objectScope);
    return `/search?${params.toString()}`;
  }, [q, objectScope]);

  const totalResultCount = useMemo(
    () => groups.reduce((n, g) => n + (g.records?.length || 0), 0),
    [groups]
  );

  /** Flat list for keyboard nav + grouped sections for rendering */
  const { flatItems, sections } = useMemo(() => {
    const items = [];
    const secs = [];

    const pushSection = (title, sectionItems) => {
      if (!sectionItems.length) return;
      secs.push({ title, items: sectionItems });
      items.push(...sectionItems);
    };

    if (!typing) {
      pushSection(
        'Recently viewed',
        recents.slice(0, 6).map((r) => ({
          key: `recent:${r.objectName}:${r.id}`,
          kind: 'recent',
          label: displayRecentTitle(r),
          hint: r.objectLabel,
          objectName: r.objectName,
          href: r.href,
          goLabel: 'Open ↵',
        }))
      );
      pushSection(
        'Apps',
        filteredApps.map((a) => ({
          key: `app:${a.id || a.name}`,
          kind: 'app',
          label: a.name,
          hint: 'App',
          href: a.id ? `/?app=${a.id}` : '/',
          app: a,
          goLabel: 'Open ↵',
        }))
      );
      pushSection(
        'Quick actions',
        [
          ...newActions.map((o) => ({
            key: `new:${o.objectName}`,
            kind: 'action',
            label: `New ${o.objectLabel}`,
            hint: 'Create',
            href: `/edit/${o.objectName}`,
            goLabel: 'Create ↵',
          })),
          ...filteredSetup.map((s) => ({
            key: `setup:${s.id}`,
            kind: 'setup',
            label: s.label || s.id,
            hint: 'Setup',
            href: s.path,
            goLabel: 'Open ↵',
          })),
        ].slice(0, 6)
      );
      return { flatItems: items, sections: secs };
    }

    const perObject = objectScope ? PER_OBJECT_SCOPED : PER_OBJECT_ALL;
    let remaining = SUGGESTION_LIMIT;
    for (const g of groups) {
      if (remaining <= 0) break;
      const slice = (g.records || []).slice(0, Math.min(perObject, remaining));
      const sectionItems = slice.map((rec) => {
        const cols = g.columns || [];
        const titleField = cols[0]?.fieldName;
        const extraParts = [];
        if (g.objectLabel) extraParts.push(g.objectLabel);
        for (let i = 1; i < Math.min(cols.length, 3); i += 1) {
          const v = rec.values?.[cols[i].fieldName];
          if (v) extraParts.push(String(v));
        }
        return {
          key: `rec:${g.objectName}:${rec.id}`,
          kind: 'record',
          label: titleField ? rec.values?.[titleField] : rec.id,
          hint: extraParts.join(' · '),
          objectName: g.objectName,
          objectLabel: g.objectLabel,
          href: rec.href,
          goLabel: 'Open ↵',
        };
      });
      pushSection(g.pluralLabel || g.objectLabel || g.objectName, sectionItems);
      remaining -= sectionItems.length;
    }

    if (!objectScope) {
      pushSection(
        'Actions & setup',
        [
          ...newActions.map((o) => ({
            key: `new:${o.objectName}`,
            kind: 'action',
            label: `New ${o.objectLabel}`,
            hint: 'Create',
            href: `/edit/${o.objectName}`,
            goLabel: 'Create ↵',
          })),
          ...filteredApps.map((a) => ({
            key: `app:${a.id || a.name}`,
            kind: 'app',
            label: a.name,
            hint: 'App',
            href: a.id ? `/?app=${a.id}` : '/',
            app: a,
            goLabel: 'Open ↵',
          })),
          ...filteredSetup.map((s) => ({
            key: `setup:${s.id}`,
            kind: 'setup',
            label: s.label || s.id,
            hint: 'Setup',
            href: s.path,
            goLabel: 'Open ↵',
          })),
        ].slice(0, 5)
      );
    }

    return { flatItems: items, sections: secs };
  }, [
    typing,
    recents,
    filteredApps,
    filteredSetup,
    newActions,
    groups,
    objectScope,
  ]);

  const runSearch = useCallback((term, scope) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const api = new GlobalSearchApi();
    const trimmed = String(term || '').trim();
    setLoading(trimmed.length >= 2);
    api
      .search({
        q: term,
        limit: SUGGESTION_LIMIT,
        objects: scope || undefined,
        signal: controller.signal,
      })
      .then((res) => {
        const data = res?.data?.data || {};
        setGroups(data.groups || []);
        setAllowedObjects(data.allowedObjects || []);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.warn('Global search failed', err);
      })
      .finally(() => {
        if (abortRef.current === controller) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    setObjectScope('');
    runSearch(q.length >= 2 ? q : '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runSearch]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query, objectScopeRef.current);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, runSearch]);

  useEffect(() => {
    if (!open) return;
    runSearch(query, objectScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectScope]);

  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems.length, query, objectScope]);

  const selectObject = useCallback(
    (objectName) => {
      setObjectScope(objectName || '');
      setTimeout(() => inputRef?.current?.focus(), 0);
    },
    [inputRef]
  );

  const cycleScope = useCallback(
    (dir) => {
      if (scopeChips.length === 0) return;
      const keys = scopeChips.map((c) => c.key);
      const idx = keys.indexOf(objectScope);
      const next = (idx + dir + keys.length) % keys.length;
      selectObject(keys[next]);
    },
    [scopeChips, objectScope, selectObject]
  );

  const go = useCallback(
    (item) => {
      if (!item?.href) return;
      onClose();
      onQueryChange('');
      setObjectScope('');
      if (item.kind === 'app' && item.app?.id) {
        try {
          localStorage.setItem('vgrow_selected_app_id', String(item.app.id));
        } catch {
          /* ignore */
        }
      }
      navigate(item.href);
    },
    [navigate, onClose, onQueryChange]
  );

  const seeAll = useCallback(() => {
    if (!q) return;
    onClose();
    onQueryChange('');
    setObjectScope('');
    navigate(viewAllHref);
  }, [q, onClose, onQueryChange, navigate, viewAllHref]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        cycleScope(e.shiftKey ? -1 : 1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(flatItems.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey || flatItems.length === 0) {
          seeAll();
          return;
        }
        const item = flatItems[activeIndex];
        if (item) go(item);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flatItems, activeIndex, go, onClose, cycleScope, seeAll]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const indexByKey = useMemo(() => {
    const map = new Map();
    flatItems.forEach((item, i) => map.set(item.key, i));
    return map;
  }, [flatItems]);

  if (!open) return null;

  const avatarFor = (item) => {
    if (item.kind === 'recent' || item.kind === 'record') {
      const tone = objectTone(item.objectName);
      return (
        <span className={`gs-av t-${tone}`} aria-hidden="true">
          {initials(item.label)}
        </span>
      );
    }
    if (item.kind === 'app') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaTh size={12} />
        </span>
      );
    }
    if (item.kind === 'setup') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaCog size={12} />
        </span>
      );
    }
    if (item.kind === 'action') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaPlus size={12} />
        </span>
      );
    }
    return (
      <span className="gs-av t-muted is-square" aria-hidden="true">
        <FaClock size={12} />
      </span>
    );
  };

  const placeholder = selectedObjectMeta
    ? `Search ${selectedObjectMeta.pluralLabel || selectedObjectMeta.objectLabel}…`
    : 'Search records, apps and actions';

  return createPortal(
    <div className="gs-overlay" onMouseDown={onClose}>
      <div
        className="gs-palette"
        role="dialog"
        aria-label="Global search"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="gs-palette-pin">
          <FaSearch className="gs-palette-pin-icon" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={placeholder}
            aria-label="Global search"
            autoComplete="off"
          />
          <kbd className="gs-palette-esc">Esc</kbd>
        </div>

        {scopeChips.length > 1 ? (
          <div className="gs-scopes" role="tablist" aria-label="Search in object">
            {scopeChips.map((chip) => (
              <button
                key={chip.key || 'all'}
                type="button"
                role="tab"
                aria-selected={objectScope === chip.key}
                className={`gs-chip${objectScope === chip.key ? ' is-on' : ''}`}
                onClick={() => selectObject(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="gs-palette-list" ref={listRef} role="listbox">
          {loading && typing ? (
            <div className="gs-palette-empty">Searching…</div>
          ) : null}

          {!loading && flatItems.length === 0 ? (
            <div className="gs-palette-empty">
              <strong>
                {typing
                  ? selectedObjectMeta
                    ? `Nothing found for “${q}”`
                    : `Nothing found for “${q}”`
                  : 'Start typing to search'}
              </strong>
              {typing
                ? 'Try fewer words, or switch to All objects.'
                : 'Recently viewed records and actions appear here.'}
            </div>
          ) : null}

          {!loading &&
            sections.map((sec) => (
              <div key={sec.title}>
                <div className="gs-palette-grp">{sec.title}</div>
                {sec.items.map((item) => {
                  const index = indexByKey.get(item.key) ?? 0;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      data-active={index === activeIndex ? 'true' : undefined}
                      className={`gs-palette-row${
                        index === activeIndex ? ' is-active' : ''
                      }`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => go(item)}
                    >
                      {avatarFor(item)}
                      <span className="gs-palette-body">
                        <span className="gs-palette-title">
                          {highlightText(item.label, query)}
                        </span>
                        {item.hint ? (
                          <span className="gs-palette-hint">
                            {highlightText(item.hint, query)}
                          </span>
                        ) : null}
                      </span>
                      <span className="gs-palette-go">{item.goLabel || 'Open ↵'}</span>
                    </button>
                  );
                })}
              </div>
            ))}
        </div>

        <div className="gs-palette-footer">
          <span>
            <kbd className="gs-kbd">↑</kbd> <kbd className="gs-kbd">↓</kbd> move
          </span>
          <span>
            <kbd className="gs-kbd">↵</kbd> open
          </span>
          <span>
            <kbd className="gs-kbd">Tab</kbd> switch object
          </span>
          <span className="gs-palette-footer-sp" />
          {typing ? (
            <button type="button" className="gs-palette-seeall" onClick={seeAll}>
              See all {totalResultCount} results
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
